// ============================================================
// Central Module Data Model – NextInvoice
// ============================================================

/** Lucide icon names used across modules */
export type LucideIconName =
  | "FileText"
  | "Bell"
  | "Users"
  | "FileSignature"
  | "BarChart2"
  | "UserCheck"
  | "Truck"
  | "Calendar"
  | "FileBadge"
  | "Package"
  | "ShoppingCart"
  | "Tag"
  | "ShoppingBag"
  | "ClipboardCheck"
  | "BookOpen"
  | "Ticket"
  | "Kanban"
  | "Shield";

// ── Categories ────────────────────────────────────────────────
export type ModuleCategory =
  | "kunden_vertrieb"
  | "finanzen"
  | "personal"
  | "warenwirtschaft"
  | "service"
  | "qm_compliance";

export const MODULE_CATEGORY_LABELS: Record<ModuleCategory, string> = {
  kunden_vertrieb: "Kunden & Vertrieb",
  finanzen: "Finanzen",
  personal: "Personal",
  warenwirtschaft: "Warenwirtschaft",
  service: "Service",
  qm_compliance: "QM & Compliance",
};

// ── Point costs ────────────────────────────────────────────────
/** 1 = leichtgewichtig, 2 = vollständig, 3 = komplex / ressourcenintensiv */
export type ModulePointType = 1 | 2 | 3;

export const POINT_LABEL: Record<ModulePointType, string> = {
  1: "1 Punkt",
  2: "2 Punkte",
  3: "3 Punkte",
};

// ── Module definition ──────────────────────────────────────────
export interface ModuleDefinition {
  id: string;
  name: string;
  shortDescription: string;
  longDescription: string;
  category: ModuleCategory;
  points: ModulePointType;
  tags: string[];
  typicalUseCases: string[];
  worksGreatWith: string[];
  icon: LucideIconName;
}

// ── Module list ────────────────────────────────────────────────
export const modules: ModuleDefinition[] = [
  // ── Kunden & Vertrieb ──────────────────────────────────────
  {
    id: "rechnungen_mahnungen",
    name: "Rechnungen & Mahnungen",
    shortDescription: "Professionelle Ausgangsrechnungen mit automatisiertem Mahnlauf.",
    longDescription:
      "Erstelle und verwalte Ausgangsrechnungen in verschiedenen Formaten (PDF, XRechnung, ZUGFeRD). Der integrierte Mahnlauf erinnert automatisch an offene Posten und generiert Mahnstufen nach eigenen Regeln.",
    category: "finanzen",
    points: 2,
    tags: ["E-Rechnung", "XRechnung", "ZUGFeRD", "Mahnwesen"],
    typicalUseCases: [
      "Monatliche Sammelrechnungen",
      "Automatischer Mahnlauf in 3 Stufen",
      "Kundenspezifische Zahlungsziele",
    ],
    worksGreatWith: ["buchhaltung_datev", "angebote", "kundenkartei"],
    icon: "FileText",
  },
  {
    id: "angebote",
    name: "Angebote",
    shortDescription: "Angebote schnell erstellen, versenden und nachverfolgen.",
    longDescription:
      "Wandle Anfragen in strukturierte Angebote um – inklusive Positions­kalkulation, Gültigkeitsdatum und direkter Konvertierung in Auftragsbestätigung oder Rechnung.",
    category: "kunden_vertrieb",
    points: 1,
    tags: ["Angebotserstellung", "Kalkulation", "Auftrag"],
    typicalUseCases: [
      "Angebote aus Artikelstamm befüllen",
      "Angebote per E-Mail versenden",
      "Konversion zu Auftragsbestätigung",
    ],
    worksGreatWith: ["rechnungen_mahnungen", "auftragsbestaetigung", "artikelstamm"],
    icon: "FileSignature",
  },
  {
    id: "kundenkartei",
    name: "Kundenkartei",
    shortDescription: "Alle Kundenstammdaten zentral und übersichtlich verwalten.",
    longDescription:
      "Pflege Kontaktdaten, Ansprechpartner, Lieferadressen und Notizen für jeden Kunden. Als Grundlage für Angebote, Rechnungen und CRM-Aktivitäten.",
    category: "kunden_vertrieb",
    points: 1,
    tags: ["Stammdaten", "Kontakte", "Adressverwaltung"],
    typicalUseCases: [
      "Stammdatenpflege B2B/B2C",
      "Mehrere Ansprechpartner pro Kunden",
      "Schnellsuche und Filter",
    ],
    worksGreatWith: ["crm", "angebote", "rechnungen_mahnungen"],
    icon: "Users",
  },
  {
    id: "kunden_vertragsverwaltung",
    name: "Kunden-Vertragsverwaltung",
    shortDescription: "Laufzeiten, Kündigungsfristen und Vertragsdokumente im Griff.",
    longDescription:
      "Hinterlege Kundenverträge mit Laufzeit, Kündigungsfristen und automatischen Erinnerungen. Verlinkung zur jeweiligen Kundenkartei und zu wiederkehrenden Rechnungen.",
    category: "kunden_vertrieb",
    points: 2,
    tags: ["Verträge", "Laufzeiten", "Kündigungsfristen"],
    typicalUseCases: [
      "SaaS-Abo-Verträge verwalten",
      "Rechtzeitig an Vertragsverlängerungen erinnert werden",
      "Vertragsarchiv pro Kunde",
    ],
    worksGreatWith: ["kundenkartei", "rechnungen_mahnungen", "crm"],
    icon: "FileBadge",
  },
  {
    id: "crm",
    name: "CRM",
    shortDescription: "Leads, Opportunities und Aktivitäten in einer Pipeline.",
    longDescription:
      "Vollständiges Customer-Relationship-Management: Lead-Erfassung, Opportunity-Pipeline, Aktivitäts-Tracking (Anrufe, Meetings, E-Mails) und Auswertungen nach Vertriebsmitarbeiter.",
    category: "kunden_vertrieb",
    points: 3,
    tags: ["Lead Management", "Pipeline", "Vertrieb", "Aktivitäten"],
    typicalUseCases: [
      "Vertriebspipeline mit Abschlusswahrscheinlichkeit",
      "Automatische Follow-up-Aufgaben",
      "Umsatzprognosen",
    ],
    worksGreatWith: ["kundenkartei", "angebote", "kunden_vertragsverwaltung"],
    icon: "BarChart2",
  },
  // ── Personal ───────────────────────────────────────────────
  {
    id: "personal",
    name: "Personal",
    shortDescription: "Mitarbeiterdaten, Abwesenheiten und Organigramm zentral.",
    longDescription:
      "Verwalte Mitarbeiterstammdaten, Arbeitszeitmodelle, Urlaubs- und Krankheitsanträge sowie Organisationsstrukturen. Basis für Schichtplanung und Vertragsverwaltung.",
    category: "personal",
    points: 3,
    tags: ["HR", "Mitarbeiterverwaltung", "Abwesenheiten"],
    typicalUseCases: [
      "Digitale Personalakte",
      "Urlaubsantrag und -genehmigung",
      "Überstundenverfolgung",
    ],
    worksGreatWith: ["schichtplanung", "personal_vertragsverwaltung", "fuhrpark"],
    icon: "UserCheck",
  },
  {
    id: "fuhrpark",
    name: "Fuhrpark",
    shortDescription: "Fahrzeuge, Wartungen und Fahrerzuweisungen verwalten.",
    longDescription:
      "Halte Fahrzeugdaten, TÜV-Termine, Wartungshistorie und Fahrerzuweisungen in einer Übersicht. Erinnerungen verhindern verpasste Wartungsintervalle.",
    category: "personal",
    points: 2,
    tags: ["Fahrzeuge", "Wartung", "Fuhrparkmanagement"],
    typicalUseCases: [
      "TÜV- und Wartungserinnerungen",
      "Fahrerzuweisung und Übergabeprotokoll",
      "Kilometerstand-Tracking",
    ],
    worksGreatWith: ["personal", "schichtplanung"],
    icon: "Truck",
  },
  {
    id: "schichtplanung",
    name: "Schichtplanung",
    shortDescription: "Dienstpläne erstellen, tauschen und kommunizieren.",
    longDescription:
      "Plane Schichten visuell per Drag-and-Drop, berücksichtige Abwesenheiten und Qualifikationen. Mitarbeiter sehen ihren aktuellen Dienstplan direkt in der App.",
    category: "personal",
    points: 2,
    tags: ["Dienstplan", "Schichten", "Personalplanung"],
    typicalUseCases: [
      "Wöchentliche Schichtrotation",
      "Schichttausch-Anfragen",
      "Qualifikationsbasierte Zuweisung",
    ],
    worksGreatWith: ["personal", "personal_vertragsverwaltung"],
    icon: "Calendar",
  },
  {
    id: "personal_vertragsverwaltung",
    name: "Personal-Vertragsverwaltung",
    shortDescription: "Arbeitsverträge, Zusatzvereinbarungen und Fristen digital.",
    longDescription:
      "Speichere Arbeitsverträge, Änderungsvereinbarungen und Kündigungen strukturiert ab. Automatische Fristenüberwachung für Probezeiten und Befristungen.",
    category: "personal",
    points: 2,
    tags: ["Arbeitsvertrag", "Probezeit", "Befristung"],
    typicalUseCases: [
      "Probezeit-Erinnerungen",
      "Befristungsauslauf-Warnungen",
      "Vertragsdokumente archivieren",
    ],
    worksGreatWith: ["personal", "schichtplanung"],
    icon: "FileBadge",
  },
  // ── Warenwirtschaft ────────────────────────────────────────
  {
    id: "lager",
    name: "Lager",
    shortDescription: "Bestände, Lagerplätze und Warenbewegungen im Überblick.",
    longDescription:
      "Verfolge Lagerbestände in Echtzeit, definiere Mindestbestände und löse automatisch Nachbestellvorschläge aus. Unterstützt mehrere Lagerorte.",
    category: "warenwirtschaft",
    points: 3,
    tags: ["Bestandsverwaltung", "Warenbewegung", "Mindestbestand"],
    typicalUseCases: [
      "Echtzeitbestände über mehrere Lager",
      "Automatische Nachbestellvorschläge",
      "Inventur-Unterstützung",
    ],
    worksGreatWith: ["einkauf", "artikelstamm", "shopanbindung"],
    icon: "Package",
  },
  {
    id: "einkauf",
    name: "Einkauf",
    shortDescription: "Bestellungen, Lieferanten und Wareneingang strukturiert abwickeln.",
    longDescription:
      "Erstelle Bestellungen aus Nachbestellvorschlägen, verwalte Lieferantenkonditionen und erfasse Wareneingänge inklusive Rechnungsprüfung.",
    category: "warenwirtschaft",
    points: 3,
    tags: ["Bestellwesen", "Lieferanten", "Wareneingang"],
    typicalUseCases: [
      "Bestellanforderungen genehmigen",
      "Lieferantenpreise vergleichen",
      "Wareneingang und Rechnungsabgleich",
    ],
    worksGreatWith: ["lager", "artikelstamm", "buchhaltung_datev"],
    icon: "ShoppingCart",
  },
  {
    id: "artikelstamm",
    name: "Artikelstamm",
    shortDescription: "Zentrales Produktverzeichnis mit Preisen, Einheiten und Varianten.",
    longDescription:
      "Pflege alle Artikel mit EAN, Lieferanten-Artikelnummer, Verkaufs- und Einkaufspreisen sowie Einheiten. Basis für Angebote, Rechnungen, Lager und Shop.",
    category: "warenwirtschaft",
    points: 2,
    tags: ["Artikel", "Produkte", "Preislisten", "EAN"],
    typicalUseCases: [
      "Zentrale Preispflege",
      "Staffelpreise pro Kunde",
      "Variantenmanagement (Größe, Farbe)",
    ],
    worksGreatWith: ["lager", "angebote", "shopanbindung"],
    icon: "Tag",
  },
  {
    id: "shopanbindung",
    name: "Shopanbindung",
    shortDescription: "Bestellungen aus Onlineshops direkt ins System übernehmen.",
    longDescription:
      "Synchronisiere Produkte, Bestände und Bestellungen bidirektional mit externen Shopsystemen. Aufträge aus dem Shop laufen automatisch in die Auftragsbearbeitung.",
    category: "warenwirtschaft",
    points: 3,
    tags: ["E-Commerce", "Shop-Sync", "Bestellimport"],
    typicalUseCases: [
      "Shopify-Bestellungen automatisch importieren",
      "Bestandsabgleich in Echtzeit",
      "Versandbenachrichtigungen zurückspielen",
    ],
    worksGreatWith: ["lager", "artikelstamm", "rechnungen_mahnungen"],
    icon: "ShoppingBag",
  },
  {
    id: "auftragsbestaetigung",
    name: "Auftragsbestätigung",
    shortDescription: "Auftragsbestätigungen aus Angeboten oder manuell erstellen.",
    longDescription:
      "Konvertiere Angebote in verbindliche Auftragsbestätigungen und versende sie automatisch. Bildet die Brücke zwischen Vertrieb und Auftragsabwicklung.",
    category: "kunden_vertrieb",
    points: 1,
    tags: ["Auftragsabwicklung", "AB", "Vertrieb"],
    typicalUseCases: [
      "Angebot → Auftragsbestätigung → Rechnung",
      "Automatischer E-Mail-Versand",
      "Lieferdatum festhalten",
    ],
    worksGreatWith: ["angebote", "rechnungen_mahnungen", "lager"],
    icon: "ClipboardCheck",
  },
  // ── Finanzen ───────────────────────────────────────────────
  {
    id: "buchhaltung_datev",
    name: "Buchhaltung & DATEV-Export",
    shortDescription: "Buchungen vorbereiten und Daten steuerfertig exportieren.",
    longDescription:
      "Erfasse Einnahmen und Ausgaben, erstelle eine EÜR-Vorschau und exportiere Buchungsdaten im DATEV-Format für den Steuerberater. Kein Ersatz für eine vollständige Finanzbuchhaltung.",
    category: "finanzen",
    points: 2,
    tags: ["Buchhaltung", "DATEV", "EÜR", "Steuerberater"],
    typicalUseCases: [
      "Monatlicher DATEV-Export zum Steuerberater",
      "EÜR-Vorschau",
      "Offene-Posten-Liste",
    ],
    worksGreatWith: ["rechnungen_mahnungen", "einkauf"],
    icon: "BookOpen",
  },
  // ── Service ────────────────────────────────────────────────
  {
    id: "ticketsystem",
    name: "Ticketsystem",
    shortDescription: "Kundenanfragen und interne Aufgaben als Tickets tracken.",
    longDescription:
      "Empfange Supportanfragen per E-Mail oder Formular, weise sie Teams zu und verfolge den Status bis zur Lösung. SLA-Zeiten konfigurierbar.",
    category: "service",
    points: 2,
    tags: ["Support", "Helpdesk", "SLA", "Tickets"],
    typicalUseCases: [
      "Kundensupport-Postfach",
      "Interne IT-Tickets",
      "SLA-Überwachung",
    ],
    worksGreatWith: ["projektmanagement", "kundenkartei"],
    icon: "Ticket",
  },
  {
    id: "projektmanagement",
    name: "Projektmanagement",
    shortDescription: "Projekte, Aufgaben und Meilensteine strukturiert steuern.",
    longDescription:
      "Plane Projekte mit Phasen, Meilensteinen und Aufgaben. Ressourcen werden Teammitgliedern zugewiesen, Fortschritte in Kanban-Boards oder Gantt-Ansicht verfolgt.",
    category: "service",
    points: 2,
    tags: ["Projekte", "Kanban", "Aufgaben", "Meilensteine"],
    typicalUseCases: [
      "Kundenprojekte mit Meilensteinen",
      "Interne Abteilungsprojekte",
      "Zeit- und Ressourcenplanung",
    ],
    worksGreatWith: ["ticketsystem", "rechnungen_mahnungen", "personal"],
    icon: "Kanban",
  },
  // ── QM & Compliance ────────────────────────────────────────
  {
    id: "qm_iso_dokumentenlenkung",
    name: "QM & ISO 9001 Dokumentenlenkung",
    shortDescription: "Dokumentenrevision, Freigaben und Prüfpläne strukturiert verwalten.",
    longDescription:
      "Verwalte qualitätsrelevante Dokumente mit Revisionsverfolgung, Freigabe-Workflows und Prüfplänen. Unterstützt Prozesse, die für Qualitätsmanagementsysteme typisch sind – ohne Zertifizierungsversprechen.",
    category: "qm_compliance",
    points: 2,
    tags: ["QM", "ISO 9001", "Dokumentenlenkung", "Revision", "Freigabe"],
    typicalUseCases: [
      "Revisionsgesteuerte Dokumente",
      "Freigabe-Workflows für QM-Dokumente",
      "Prüfplan-Verwaltung",
    ],
    worksGreatWith: ["projektmanagement", "personal"],
    icon: "Shield",
  },
];

// ── Derived helpers ────────────────────────────────────────────

/** All selectable module IDs */
export const ALL_MODULE_IDS = modules.map((m) => m.id);

/** Total points for a given set of module IDs */
export function calcUsedPoints(selectedIds: string[]): number {
  return modules
    .filter((m) => selectedIds.includes(m.id))
    .reduce((sum, m) => sum + m.points, 0);
}

/** Group modules by category */
export function getModulesByCategory(): Record<ModuleCategory, ModuleDefinition[]> {
  const result = {} as Record<ModuleCategory, ModuleDefinition[]>;
  for (const cat of Object.keys(MODULE_CATEGORY_LABELS) as ModuleCategory[]) {
    result[cat] = modules.filter((m) => m.category === cat);
  }
  return result;
}
